import SvgLabelPrinting from '../../../components/icons/SvgLabelPrinting';
import {
  SvgNotification,
  SvgSignature,
  SvgDeliveryGuarantee,
  SvgProtectionAdd,
  SvgProtection,
  SvgPrintInStore,
  SvgGlobe,
  SvgEmail,
  SvgCalendarOneDay,
  SvgWarningTriangle
} from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import SvgCarbonNeutral from '~/components/icons/SvgCarbonNeutral';

export const ExtendedBaseCoverExtra = 'ExtendedBaseCover';
export const CoverExtra = 'Cover';
export const SignatureExtra = 'Signature';
export const PrintInStoreExtra = 'PrintInStore';
export const SmsExtra = 'Sms';
export const SameDayCollectionExtra = 'SameDayCollection';
export const DeliveredDutyPaidExtra = 'DeliveredDutyPaid';
export const DeliveryGuaranteeExtra = 'DeliveryGuarantee';
export const DriverBringsLabelExtra = 'DriverBringsLabel';
export const ThirdPartyEmail = 'ThirdPartyEmail';
export const Oversize = 'Oversize';

// UPS DAP extra for non-standard parcels. Lowercase deliberately: the edge's ConvertToLegacyExtraType
// only maps a fixed legacy set and passes every other quoting key straight through, so this Id arrives
// exactly as the quoting API spells it - unlike the PascalCase legacy Ids above. Not to be confused with
// `Oversize`, which is an unrelated legacy P2G surcharge listed in HiddenExtras.
export const CarbonNeutralExtra = 'carbonneutral';
export const AdditionalHandlingExtra = 'additionalhandling';
export const EuCustomsFee = 'eucustomsfee';
export const CustomsHandlingFee = 'customshandlingfee';
export const OriginResidentialExtra = 'originresidential';
export const DestinationResidentialExtra = 'destinationresidential';

export const ProtectionExtraIds = [ExtendedBaseCoverExtra, CoverExtra];

// Hidden from the Edit Panel
export const ExtrasWithCustomUi = [
  ExtendedBaseCoverExtra,
  CoverExtra,
  DeliveredDutyPaidExtra,
  SameDayCollectionExtra,
  EuCustomsFee,
  CustomsHandlingFee,
  OriginResidentialExtra,
  DestinationResidentialExtra
];

// Legacy Surcharge Style Extras. Ideally removed from Edge Api.
// Hidden on the MarketplaceDetailsRow
export const HiddenExtras = [
  Oversize,
  EuCustomsFee,
  CustomsHandlingFee,
  OriginResidentialExtra,
  DestinationResidentialExtra,
  AdditionalHandlingExtra
];

export const PackageExtras = [AdditionalHandlingExtra];

export function ExtrasIcon(props: { Id: string }) {
  switch (props.Id) {
    case SmsExtra:
      return <SvgNotification />;
    case SameDayCollectionExtra:
      return <SvgCalendarOneDay />;
    case SignatureExtra:
      return <SvgSignature />;
    case DeliveryGuaranteeExtra:
      return <SvgDeliveryGuarantee />;
    case DriverBringsLabelExtra:
      return <SvgLabelPrinting />;
    case CoverExtra:
      return <SvgProtectionAdd />;
    case ExtendedBaseCoverExtra:
      return <SvgProtection />;
    case PrintInStoreExtra:
      return <SvgPrintInStore />;
    case DeliveredDutyPaidExtra:
      return <SvgGlobe />;
    case ThirdPartyEmail:
      return <SvgEmail />;
    case AdditionalHandlingExtra:
      return <SvgWarningTriangle />;
    case CarbonNeutralExtra:
      return <SvgCarbonNeutral />;
    default:
      return null;
  }
}

export function ExtrasIconSrc(props: { Id: string }) {
  switch (props.Id) {
    case SmsExtra:
      return `/theme/icons/Notification.svg`;
    case SignatureExtra:
      return `/theme/icons/Signature.svg`;
    case DeliveryGuaranteeExtra:
      return `/theme/icons/DeliveryGuarantee.svg`;
    case DriverBringsLabelExtra:
      return `/theme/icons/LabelPrinting.svg`;
    case CoverExtra:
      return `/theme/icons/ProtectionAdd.svg`;
    case ExtendedBaseCoverExtra:
      return `/theme/icons/Protection.svg`;
    case PrintInStoreExtra:
      return `/theme/icons/PrintInStore.svg`;
    case DeliveredDutyPaidExtra:
      return `/theme/icons/Customs.svg`;
    case ThirdPartyEmail:
      return `/theme/icons/Email.svg`;
  }
  return null;
}

export function ExtrasTitle(props: { Id: string; Name: string }) {
  switch (props.Id) {
    case ExtendedBaseCoverExtra:
      return `Basic\nProtection`;
    case CoverExtra:
      return `Full\nProtection`;
    case SignatureExtra:
      return `Proof of\nDelivery`;
    case SmsExtra:
      return `Delivery\nConfirmation`;
    case PrintInStoreExtra:
      return `Print In\nStore`;
    case DeliveredDutyPaidExtra:
      return `Delivered\nDuty Paid`;
    case DeliveryGuaranteeExtra:
      return `Delivery\nGuarantee`;
    case DriverBringsLabelExtra:
      return `Driver\nBrings Label`;
    case ThirdPartyEmail:
      return `Delivery\nEmail`;
    case AdditionalHandlingExtra:
      return `Additional\nHandling`;
    case CarbonNeutralExtra:
      return `Carbon\nNeutral`;
    default:
      return props.Name || props.Id;
  }
}

export function ExtrasDescription(props: { Id: string; Name: string }) {
  switch (props.Id) {
    case ExtendedBaseCoverExtra:
      return `Protect your parcel with ${toDisplayCurrency(null, 20)} protection.`;
    case CoverExtra:
      return 'Fully Protect your parcel (service limits apply).';
    case SignatureExtra:
      return 'Add a signature to guarantee your parcel has been received.';
    case SmsExtra:
      return 'Receive text message updates.';
    case SameDayCollectionExtra:
      return 'Same day collection.';
    case PrintInStoreExtra:
      return 'No printer, no problem, take your email confirmation with QR code, to your nearest print in store shop.';
    case DeliveryGuaranteeExtra:
      return "Receive a refund of the carriage if your parcel doesn't arrive on-time.";
    case DriverBringsLabelExtra:
      return 'Have the driver bring your label(s) on collection.';
    case DeliveredDutyPaidExtra:
      return 'Would you like to pay for any tax and import duties before you send your parcel?';
    case ThirdPartyEmail:
      return 'Would you like the recipient to receive a delivery Email from the courier?';
    case AdditionalHandlingExtra:
      return 'For non-standard parcels - non-boxed, oversized or heavy items the carrier must handle separately from normal automated sorting.';
    case CarbonNeutralExtra:
      return 'Make your shipment carbon neutral.';
    default:
      return null;
  }
}
